import { useLocation } from 'preact-iso'
import { AlertTriangle } from 'lucide-preact'
import type { WebCardDto } from '@/types/campaign'
import { getWebTypeMeta } from '@/constants/webTypes'
import { summariseOutstanding, isTitleOutstanding } from '@/constants/signOffReadiness'
import { LeadGoalBar } from '@/components'
import { formatDateOnly } from '@/utils/dateTimeFormatters'
import { shortAgo } from '@/utils/time'
import type { JSX } from 'preact'
import { routes } from '@/utils/routes'
import { BRAND_CLASSES } from '@/constants/theme'

interface CampaignWebsCardViewProps {
    webs: WebCardDto[]
    campaignId: string
    workspaceId: string
    clientCompanyId: string
}

export function CampaignWebsCardView({ webs, campaignId, workspaceId, clientCompanyId }: CampaignWebsCardViewProps) {
    const location = useLocation()

    const getLaunchText = (launchDate: string): string => {
        if (!launchDate) return 'Launch —'
        
        // Get today's date as YYYY-MM-DD in local time
        const today = new Date()
        const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
        
        if (launchDate === todayStr) {
            return 'Launch is today'
        } else if (launchDate < todayStr) {
            return `Launched ${formatDateOnly(launchDate)}`
        } else {
            return `Launch is ${formatDateOnly(launchDate)}`
        }
    }

    const Card = ({ item }: { item: WebCardDto }) => {
        const tone = 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600'

        // Map WebType to icon/label
        const typeMeta = getWebTypeMeta(item.WebType)
        const Icon = typeMeta.Icon
        const chipClass = typeMeta.chipClass
        const displayLabel = item.WebType.trim()

        const goal = Math.max(0, item.LeadGoal || 0)
        const leads = Math.max(0, item.LeadsCount || 0)
        const outstandingCount = item.OutstandingForSignOff?.length ?? 0

        const url = routes.scoped(clientCompanyId, workspaceId).web(campaignId, item.Id)
        const open = () => location.route(url)

        return (
            <div
                role="button"
                tabIndex={0}
                onClick={open}
                onKeyDown={(e: JSX.TargetedKeyboardEvent<HTMLDivElement>) => { 
                    if (e.key === 'Enter' || e.key === ' ') { 
                        e.preventDefault()
                        open() 
                    } 
                }}
                className={`rounded-xl border p-5 shadow-sm hover:shadow-lg transition-all duration-200 w-full text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 ${tone}`}
            >
                <div>
                    <div className="flex-1 min-w-0">
                        <div className="mb-2">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-sm font-semibold rounded-full ${chipClass}`}>
                                <Icon className="w-4 h-4" />
                                {displayLabel}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 mb-2">
                            {/* Same reason as the list view: a web created before the content call has no
                                title, and the API falls back to the internal reference name. */}
                            {isTitleOutstanding(item.OutstandingForSignOff) ? (
                                <h3 className="text-sm md:text-base font-mono text-slate-400 dark:text-slate-500 truncate">
                                    {item.Name} <span className="font-sans text-xs">— title not set</span>
                                </h3>
                            ) : (
                                <h3 className="text-sm md:text-base font-semibold truncate">{item.Title}</h3>
                            )}
                            {item.ConferenceNotFound && (
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${BRAND_CLASSES.STATUS.SYNC_ERROR}`}>
                                    Sync Error
                                </span>
                            )}
                        </div>
                        {/* With no goal there is nothing to be a percentage of, so the bar is
                            omitted rather than drawn empty — an empty bar reads as "0% of the way
                            there", which is the opposite of the truth when leads exist. Filling it
                            instead would make a full bar meaningless, since every web without a
                            goal would show one. */}
                        <div className="mt-1">
                            <div className="flex items-center justify-between text-xs md:text-sm mb-1">
                                <span className="text-slate-500 dark:text-slate-400">Leads generated</span>
                                <span className="font-medium">
                                    {goal > 0 ? `${leads.toLocaleString()} / ${goal.toLocaleString()}` : leads.toLocaleString()}
                                </span>
                            </div>
                            {goal > 0 ? (
                                <LeadGoalBar sourced={leads} goal={goal} mode="card" />
                            ) : (
                                <span className="text-xs text-slate-400 dark:text-slate-500">No lead goal set</span>
                            )}
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs md:text-sm text-slate-500 dark:text-slate-400">
                            <span>{getLaunchText(item.LaunchDate)}</span>
                            <span>{item.LastLeadCreatedDate ? shortAgo(item.LastLeadCreatedDate) : 'No leads'}</span>
                        </div>
                        {/* Cards are the default view under ten webs, so the readiness signal has to be
                            here too — otherwise most users never see it. Count only; the detail lives on
                            the web itself, which is one click away. */}
                        {!!outstandingCount && (
                            <div
                                className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                                title={summariseOutstanding(item.OutstandingForSignOff)}
                            >
                                <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                                {outstandingCount} before sign-off
                            </div>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
            {webs.map(web => (
                <Card key={web.Id} item={web} />
            ))}
        </div>
    )
}
